import React from "react";
import { SliceComponentProps } from "@prismicio/react";

import { AVAILABLE_LANGS, SERVICE_TYPE } from "@typings/globals";
import { DEFAULT_CLEANING_TYPE } from "@typings/user";

import * as Grid from "@ui/Atoms/Grid";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { StartBookingForm } from "@containers/StartBookingForm";

import { useAuth } from "@hooks/useAuth";
import { useTranslate } from "@hooks/useTranslate";

import { PrismicLandingPageV2DataBodyStartbooking } from "@www/graphql-types";

import * as Styled from "./StartBooking.styled";
import { CleaningSelectorItem } from "./StartBooking.CleaningSelectorItem";

const cleaningTypeToServiceType: Record<DEFAULT_CLEANING_TYPE, SERVICE_TYPE> = {
  [DEFAULT_CLEANING_TYPE.B2C]: SERVICE_TYPE.HOME_CLEANING,
  [DEFAULT_CLEANING_TYPE.B2B]: SERVICE_TYPE.B2B,
  [DEFAULT_CLEANING_TYPE.EOT]: SERVICE_TYPE.END_OF_TENANCY,
};

export const START_BOOKING_ID = "start-booking";

export const StartBooking = (
  props: SliceComponentProps<
    PrismicLandingPageV2DataBodyStartbooking,
    { language: AVAILABLE_LANGS }
  >,
) => {
  const language = props.context.language;
  const { primary, items } = props.slice;
  const { isUserDataFetched, user } = useAuth();
  const translate = useTranslate("batmaid_pwa_generic");

  const [selectedCleaningType, setSelectedCleaningType] =
    React.useState<SERVICE_TYPE | null>(null);

  React.useEffect(() => {
    if (!isUserDataFetched || !!selectedCleaningType) return;

    const newSelectedCleaningType =
      cleaningTypeToServiceType[
        user?.get("defaultCleaningType") || DEFAULT_CLEANING_TYPE.B2C
      ];
    setSelectedCleaningType(newSelectedCleaningType);
  }, [isUserDataFetched, selectedCleaningType, user]);

  const submitHandler = (url: string) => {
    if (!window) return;

    window.location.href = url;
  };

  return (
    <TestWrapper testId="startBookingSection">
      <Styled.Root
        id={START_BOOKING_ID}
        widthVariant="full"
        style={{
          backgroundColor:
            primary.startbooking_background_color || "transparent",
        }}
      >
        <Grid.Container>
          <Grid.Row>
            <Grid.Column md={10} pushMd={1} lg={8} pushLg={2}>
              <Styled.Container>
                <Styled.Title as="h2" $style="bold_32">
                  {primary.startbooking_title.text}
                </Styled.Title>

                <Styled.Body>
                  <Styled.CleaningSelector>
                    {items.map(
                      ({
                        startbooking_service_type,
                      }: {
                        startbooking_service_type: SERVICE_TYPE;
                      }) => (
                        <CleaningSelectorItem
                          key={startbooking_service_type}
                          serviceType={startbooking_service_type}
                          selected={
                            selectedCleaningType === startbooking_service_type
                          }
                          onClick={setSelectedCleaningType}
                        />
                      ),
                    )}
                  </Styled.CleaningSelector>

                  <Styled.FormWrapper>
                    <StartBookingForm
                      serviceType={
                        selectedCleaningType || SERVICE_TYPE.HOME_CLEANING
                      }
                      ssgLanguage={language}
                      urlType="absolute"
                      eventId="start_booking_submit_zip"
                      buttonVariant="green"
                      translations={{
                        buttonLabel:
                          primary.startbooking_button_label.text ||
                          translate("button_lets_go"),
                        fieldPlaceholder: translate("enter_your_postal_code"),
                      }}
                      onSuccess={submitHandler}
                      buttonFitContent
                    />
                  </Styled.FormWrapper>
                </Styled.Body>
              </Styled.Container>
            </Grid.Column>
          </Grid.Row>
        </Grid.Container>
      </Styled.Root>
    </TestWrapper>
  );
};
